import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Users, Maximize, ShieldCheck, Play } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";
import { api, WHATSAPP_URL } from "../lib/api";

export default function ExperienceDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setItem(null); setMissing(false);
    api.get("experiences", slug).then(setItem).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return (
    <main className="pt-48 pb-40 text-center">
      <p className="font-serif text-3xl mb-6">Experience not found.</p>
      <Link to="/experiences" className="text-sm text-zinc-400 hover:text-white">← Back to Experiences</Link>
    </main>
  );
  if (!item) return <main className="pt-48 pb-40 text-center text-zinc-500 font-mono text-sm">Loading…</main>;

  const specs = [
    [Clock, "Duration", item.duration], [ShieldCheck, "Recommended Age", item.age],
    [Users, "Participants", item.participants], [Maximize, "Space Requirement", item.space],
  ];

  return (
    <main>
      <Seo title={item.name} path={`/experiences/${slug}`} description={item.tagline} />
      <section className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="experience-hero">
        <AbstractVisual hue={item.hue ?? 222} seed={slug.length * 13} />
        <ParticleField hue={item.hue ?? 222} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-[#030305]/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Link to="/experiences" data-testid="back-to-experiences" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors mb-10">
              <ArrowLeft size={14} /> Experiences
            </Link>
          </motion.div>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border border-white/15 backdrop-blur-md bg-black/30">{item.category}</span>
            {item.status && <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-black bg-white/90">{item.status}</span>}
          </div>
          <h1 className="font-serif tracking-tight leading-[0.98] text-5xl md:text-7xl lg:text-8xl" data-testid="experience-title">
            <MaskedLine delay={0.25}>{item.name}</MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-6 text-lg text-zinc-400 font-light max-w-xl">{item.tagline}</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }} className="mt-9 flex flex-wrap gap-4">
            <button data-testid="watch-trailer-button"
              className="px-7 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors inline-flex items-center gap-2">
              <Play size={15} /> Watch Trailer
            </button>
            <a href={WHATSAPP_URL(`Hi Vrova, I'd like to experience "${item.name}".`)} target="_blank" rel="noreferrer"
              data-testid="experience-whatsapp-cta"
              className="px-7 py-3.5 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors">
              Enquire on WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline className="mb-5">About This Experience</Overline>
              <p className="text-lg md:text-xl text-zinc-300 font-light leading-relaxed">{item.description}</p>
              {item.placeholder && (
                <p className="mt-8 text-xs font-mono text-zinc-600 border border-white/10 rounded-lg px-4 py-3 inline-block">
                  Showcase content — this listing demonstrates the experience format and will feature licensed titles.
                </p>
              )}
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl divide-y divide-white/[0.07]" data-testid="experience-specs">
                {specs.map(([Icon, label, value]) => (
                  <div key={label} className="flex items-center justify-between px-7 py-5">
                    <span className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.15em] text-zinc-500"><Icon size={15} />{label}</span>
                    <span className="text-sm text-zinc-200">{value}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/[0.06] text-center">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Want This Experience <span className="italic text-zinc-400">at Your Venue?</span></h2>
          <Link to="/venues" data-testid="experience-venue-cta"
            className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors">
            Install This Experience <ArrowRight size={15} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
